import { fetchLeaderboardAPIPath, fetchMyLeaderboardPositionAPIPath, fetchMyShareAnalyticsAPIPath, fetchMyTopSharesAPIPath, generatePersonalBrandVoiceAPIPath } from "../links"
import { getDateFromRelativeString, getDateString } from "../utils"

export async function logout(csrfToken, { platform }) {
  try {
    let res = await fetch(`/api/social-login/${platform}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
    })

    if (res.status === 401) {
      return {
        error: "unauthorized",
      }
    }

    if (res.status >= 400) {
      let result = await res.json()
      return {
        error: result.Error,
      }
    }

    return {}
  } catch (e) {
    console.error("error while logging out", e)
    return {
      error: "Error while logging out. Please try again in sometime."
    }
  }
}

export async function updateSettings(csrfToken, settings) {
  try {
    let res = await fetch(`/api/advocate/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(settings),
    })

    if (res.status === 401) {
      return {
        error: "unauthorized",
      }
    }

    if (res.status >= 400) {
      let result = await res.json()
      return {
        error: result.Error,
      }
    }

    return {}
  } catch (e) {
    console.error("error while updating settings", e)
    return {
      error: "Error while updating settings. Please try again in sometime."
    }
  }
}

export async function generatePersonalBrandVoice(csrfToken, orgID) {
  try {
    const res = await fetch(generatePersonalBrandVoiceAPIPath({ orgID }), {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    })
    const result = await res.json()
    if (res.status >= 400) return { error: result.Error }
    return {}
  } catch (e) {
    console.error("error while generating personal brand voice", e)
    return { error: "Failed to generate personal brand voice. Please try again." }
  }
}

export async function deletePersonalBrandVoice(csrfToken) {
  try {
    const res = await fetch(`/api/advocate/personal-brand-voice`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    })
    if (res.status >= 400) {
      const result = await res.json()
      return { error: result.Error }
    }
    return {}
  } catch (e) {
    console.error("error while deleting personal brand voice", e)
    return { error: "Failed to delete personal brand voice. Please try again." }
  }
}

export async function fetchLeaderboard({ orgID, metric, limit, offset, start, end }) {
  try {
    let timePeriodStart, timePeriodEnd
    if (start) timePeriodStart = getDateString(getDateFromRelativeString(start))
    if (end) timePeriodEnd = getDateString(getDateFromRelativeString(end))
    const res = await fetch(fetchLeaderboardAPIPath({ orgID, metric, limit, offset, timePeriodStart, timePeriodEnd }))
    const result = await res.json()
    if (res.status >= 400) return { error: result.Error }
    return { leaderboard: result.Data }
  } catch (e) {
    console.error('error fetching leaderboard', e)
    return { error: 'Failed to load leaderboard. Please try again.' }
  }
}

export async function fetchMyLeaderboardPosition({ orgID, metric, limit, start, end }) {
  try {
    let timePeriodStart, timePeriodEnd
    if (start) timePeriodStart = getDateString(getDateFromRelativeString(start))
    if (end) timePeriodEnd = getDateString(getDateFromRelativeString(end))
    const res = await fetch(fetchMyLeaderboardPositionAPIPath({ orgID, metric, limit, timePeriodStart, timePeriodEnd }))
    const result = await res.json()
    if (res.status >= 400) return { error: result.Error }
    return { leaderboard: result.Data }
  } catch (e) {
    console.error('error fetching leaderboard position', e)
    return { error: 'Failed to load leaderboard position. Please try again.' }
  }
}

export async function fetchMyShareAnalytics({ orgID, CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd }) {
  try {
    const res = await fetch(fetchMyShareAnalyticsAPIPath({ orgID, CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd }))
    const result = await res.json()
    if (res.status >= 400) return { error: result.Error }
    return { shareAnalytics: result.Data }
  } catch (e) {
    console.error('error fetching share analytics', e)
    return { error: 'Failed to load share analytics. Please try again.' }
  }
}

export async function fetchMyTopShares({ orgID, limit, offset, sortOrder, start, end }) {
  try {
    let timePeriodStart, timePeriodEnd
    if (start) {
      timePeriodStart = getDateString(getDateFromRelativeString(start))
    }
    if (end) {
      timePeriodEnd = getDateString(getDateFromRelativeString(end))
    }

    const res = await fetch(fetchMyTopSharesAPIPath({ orgID, limit, offset, sortOrder, timePeriodStart, timePeriodEnd }))
    const result = await res.json()
    if (res.status >= 400) return { error: result.Error }
    return { shares: result.Data }
  } catch (e) {
    console.error('error fetching top shares', e)
    return { error: 'Failed to load shares. Please try again.' }
  }
}

export async function fetchPublicPostVariation({ shareToken, postVariationID }) {
  try {
    const res = await fetch(`/api/public/posts/${shareToken}/post-variations/${postVariationID}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
    if (res.status >= 400) {
      const result = await res.json()
      return { error: result.Error }
    }
    const result = await res.json()
    return { variation: result.Data }
  } catch (e) {
    console.error("error while fetching public variations", e)
    return { error: "Error while fetching variations. Please try again." }
  }
}

export async function fetchVariation({ postID, postVariationID, orgID }) {
  try {
    let url = `/api/posts/${postID}/post-variations/${postVariationID}`
    const queryParams = new URLSearchParams()
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`
    }


    let res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (res.status >= 400) {
      let result = await res.json()
      return {
        error: result.Error,
      }
    }

    let result = await res.json()
    return {
      variation: result.Data,
    }
  } catch (e) {
    console.error("error while fetching variations", e)
    return {
      error: "Error while fetching variations. Please try again in sometime."
    }
  }
}

export async function getMyLatestImportBatch() {
  try {
    const res = await fetch('/api/me/social-media-shares/import-batches/latest')
    if (res.status >= 400) {
      const result = await res.json()
      return { error: result.Error }
    }
    const result = await res.json()
    return { data: result.Data }
  } catch (e) {
    console.error('error fetching import batch', e)
    return { error: 'Error while fetching import status.' }
  }
}

export async function importMyShares(csrfToken, shares) {
  try {
    const res = await fetch('/api/me/social-media-shares/bulk-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ Shares: shares }),
    })
    if (res.status >= 400) {
      const result = await res.json()
      return { error: result.Error }
    }
    const result = await res.json()
    return { data: result.Data }
  } catch (e) {
    console.error('error importing shares', e)
    return { error: 'Error while importing shares. Please try again.' }
  }
}

// fetchMyCalendarItems returns the shares of the advocate and the posts waiting for their approval between from and to
export async function fetchMyCalendarItems({ orgID, from, to }) {
  try {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() })
    if (orgID) {
      query.set("orgID", orgID)
    }
    const res = await fetch(`/api/me/calendar?${query.toString()}`)
    const data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || "Error while fetching your calendar. Please try again in sometime." }
    }
    return { items: data.Data?.Items || [] }
  } catch (e) {
    console.error("error while fetching my calendar", e)
    return { error: "Error while fetching your calendar. Please try again in sometime." }
  }
}
